import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  IconButton,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
  ListItemIcon,
} from "@mui/material";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import { globalVariables } from "../../../global/GlobalVariables";
import { BCPAngleDown, BCPBars } from "../../icons/bcp/BCPIcons";
import StyledMenu from "../StyledMenu";
import { useSetIsLogin } from "../../user/BCPAuthReducers";
import NavItem from "../../../global/NavItem";
import ProfileMenu from "../ProfileMenu";

// TODO: Will need to add back any catan, primal, and UYS conditionals. Refer to MainHeaderLegacy.js for examples.

const LARGE_MENU = getEnvironmentVariable("LARGE_MENU") === "true";
const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);
const HIDE_BANNER_COMPANY_TITLE =
  getEnvironmentVariable("HIDE_BANNER_COMPANY_TITLE") !== "true";
const HAS_LARGE_BANNER_LOGO =
  getEnvironmentVariable("HAS_LARGE_BANNER_LOGO") === "true";

function TopHeaderMenu(props) {
  if (!globalVariables().menuArrayTop?.length) {
    return <></>;
  }

  return (
    <Hidden mdDown>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar
          elevation={0}
          sx={{
            backgroundColor: "banner.main",
            color: "banner.contrastText",
            zIndex: 1299,
            width: "100%",
          }}
        >
          <Toolbar
            sx={{
              minHeight: "52px !important",
              maxHeight: "52px !important",
              "& .MuiMenuItem-root:not(:first-child)": {
                paddingLeft: 1,
              },
              "& .MuiMenuItem-root:not(:last-child)": {
                paddingRight: 1,
              },
              "& .MuiMenuItem-root:first-child": {
                paddingLeft: 0,
              },
              "& .MuiMenuItem-root:last-child": {
                paddingRight: 0,
              },
            }}
          >
            {globalVariables().menuArrayTop.map((menuItem, index) => {
              return (
                <MenuItem
                  key={index}
                  href={menuItem.href}
                  component="a"
                  target={menuItem.target}
                  disableRipple
                  sx={{ fontSize: "12px" }}
                >
                  {menuItem.title}
                </MenuItem>
              );
            })}
          </Toolbar>
        </AppBar>
      </Box>
    </Hidden>
  );
}

const isCurrentHref = (href) => {
  return (
    href.split("?")[0].trim().toLowerCase() ===
    window.location.pathname.split("?")[0].trim().toLowerCase()
  );
};

const HeaderMenuItem = (props) => {
  const {
    title,
    handleParentClose,
    handleClick,
    onClick,
    children,
    sx,
    divider,
    ...rest
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    if (handleParentClose) {
      handleParentClose();
    }
  };

  if (divider) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 1 }}>
        <Divider
          sx={{
            width: "100%",
            ...sx,
          }}
        />
      </Box>
    );
  }

  return (
    <MenuItem
      disableRipple
      sx={{
        ...sx,
      }}
      onClick={
        handleClick || onClick || children?.length > 0 ? handleMenuClick : null
      }
      component={props.href ? Link : null}
      {...rest}
    >
      {title}

      {children?.length > 0 && (
        <>
          <ListItemIcon>
            <BCPAngleDown style={{ "font-size": "14px", width: "14px" }} />
          </ListItemIcon>
          <StyledMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {children.map((child, index) => {
              return (
                <HeaderMenuItem
                  key={index}
                  handleParentClose={handleClose}
                  {...child}
                />
              );
            })}
          </StyledMenu>
        </>
      )}
    </MenuItem>
  );
};

const HeaderMenuButton = (props) => {
  const { onClick, title, children, href, target, sx } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (children) {
    return (
      <>
        <Button
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          variant="contained"
          color="banner"
          disableElevation
          onClick={handleMenuClick}
          endIcon={
            <BCPAngleDown style={{ "font-size": "14px", width: "14px" }} />
          }
          sx={{
            minWidth: "unset",
            ...(href && isCurrentHref(href) ? { color: "link.main" } : {}),
            ...sx,
          }}
        >
          <Typography variant="h5">{title}</Typography>
        </Button>

        <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {children.map((child, index) => {
            return (
              <HeaderMenuItem
                key={index}
                handleParentClose={handleClose}
                {...child}
              />
            );
          })}
        </StyledMenu>
      </>
    );
  } else if (href) {
    return (
      <Button
        color="banner"
        variant="contained"
        component={Link}
        href={href}
        target={target}
        disableElevation
        sx={{
          minWidth: "unset",
          ...(isCurrentHref(href) ? { color: "link.main" } : {}),
          ...sx,
        }}
      >
        <Typography variant="h5">{title}</Typography>
      </Button>
    );
  } else {
    return (
      <Button
        color="banner"
        variant="contained"
        disableElevation
        onClick={onClick}
        sx={{
          minWidth: "unset",
          ...sx,
        }}
      >
        <Typography variant="h5">{title}</Typography>
      </Button>
    );
  }
};

export default function MainHeaderBCP(props) {
  const {
    drawerOpen,
    setDrawerOpen,
    authContext,
    itemsTop,
    itemsMiddleTop,
    itemsMiddle,
    itemsBottom,
  } = props;

  const setIsLogin = useSetIsLogin();

  const navigate = useNavigate();
  const theme = useTheme();

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const Content = (props) => {
    const { hideTop, hideMiddleTop, hideMiddle, hideBottom } = props;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box>
          <List>
            {/* <NavItem href="/home" icon={BCPHouse} title="Home" />

            {(itemsTop || itemsMiddleTop) && !hideTop && (
              <Divider
                key={`bottom-divider`}
                sx={{ mt: 1, mb: 1, ml: "-32px", mr: "-32px" }}
              />
            )} */}

            {!hideTop &&
              itemsTop.map((item, index) => {
                if (item?.divider) {
                  return (
                    <Divider
                      key={`top-divider-${index}`}
                      sx={{
                        mt: 2,
                        mb: 2,
                        ml: "-32px",
                        mr: "-32px",
                        ...item?.sx,
                      }}
                    />
                  );
                }
                if (item?.title === "Subscribe") return <></>;
                return <NavItem key={index} {...item} />;
              })}
            {!hideMiddleTop &&
              itemsMiddleTop &&
              itemsMiddleTop.map((item, index) => {
                if (item?.divider) {
                  return (
                    <Divider
                      key={`middle-top-divider-${index}`}
                      sx={{
                        mt: 2,
                        mb: 2,
                        ml: "-32px",
                        mr: "-32px",
                        ...item?.sx,
                      }}
                    />
                  );
                }
                return <NavItem key={index} {...item} />;
              })}
          </List>
        </Box>
        {itemsMiddle && itemsMiddle.length > 0 && <Divider />}
        {!hideMiddle && itemsMiddle && itemsMiddle.length > 0 && (
          <Box>
            <List>
              {itemsMiddle.map((item, index) => {
                if (item?.divider) {
                  return (
                    <Divider
                      key={`middle-divider-${index}`}
                      sx={{
                        mt: 2,
                        mb: 2,
                        ml: "-32px",
                        mr: "-32px",
                        ...item?.sx,
                      }}
                    />
                  );
                }
                return <NavItem key={index} {...item} />;
              })}
            </List>
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {!hideBottom && itemsBottom && itemsBottom.length > 0 && (
          <Box>
            <Divider />
            <List>
              {itemsBottom.map((item, index) => {
                if (item?.divider) {
                  return (
                    <Divider
                      key={`bottom-divider-${index}`}
                      sx={{
                        mt: 2,
                        mb: 2,
                        ml: "-32px",
                        mr: "-32px",
                        ...item?.sx,
                      }}
                    />
                  );
                }
                return <NavItem key={index} {...item} />;
              })}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <TopHeaderMenu />
      <AppBar
        elevation={0}
        sx={{
          backgroundColor: "banner.main",
          color: "banner.contrastText",
          zIndex: 1299,
          width: "100%",
          top: globalVariables().menuArrayTop?.length
            ? [null, null, "52px"]
            : null,
          display: ["flex", "flex", "", ""],
          justifyContent: "center",
          alignItems: "space-between",
        }}
      >
        <Toolbar>
          <Box
            sx={{
              flexGrow: [1, 1, LARGE_MENU ? 1 : "unset", "unset"],
              textAlign: [
                "center",
                "center",
                LARGE_MENU ? "center" : "unset",
                "unset",
              ],
            }}
          >
            {
              <Link href={"/"} underline="none">
                <Hidden lgDown>
                  <img
                    src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/BannerLogo${
                      theme.palette.mode === "dark" ? "Dark" : ""
                    }${HAS_LARGE_BANNER_LOGO ? "Large" : ""}.png`}
                    alt="logo"
                    style={{
                      maxHeight: "45px",
                      marginRight: "8px",
                    }}
                  />
                </Hidden>
                <Hidden lgUp>
                  <img
                    src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/BannerLogo${
                      theme.palette.mode === "dark" ? "Dark" : ""
                    }.png`}
                    alt="logo"
                    style={{
                      maxHeight: "45px",
                      marginRight: "8px",
                    }}
                  />
                </Hidden>
              </Link>
            }
          </Box>
          <Hidden mdDown lgDown={LARGE_MENU}>
            {HIDE_BANNER_COMPANY_TITLE && (
              <Box>
                <Link
                  href="/"
                  underline="none"
                  sx={{ textDecoration: "none !important", color: "inherit" }}
                >
                  <Typography variant="h4">
                    {globalVariables().site.title}
                  </Typography>
                </Link>
              </Box>
            )}
            {getEnvironmentVariable("LEFT_ALIGN_MENU") !== "true" && (
              <Box sx={{ flexGrow: 1 }}></Box>
            )}
            <Box sx={{ pr: 3 }}>
              {itemsTop
                .filter((item) => {
                  if (
                    (item?.requires?.isAuthenticated === true &&
                      !authContext?.authState?.isAuthenticated) ||
                    (item?.requires?.isAuthenticated === false &&
                      authContext?.authState?.isAuthenticated)
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((item, index) => {
                  if (item?.title === "Account") {
                    return (
                      <>
                        {!authContext?.authState?.isAuthenticated ? (
                          <Hidden mdDown>
                            <HeaderMenuButton
                              title="Login"
                              onClick={() => {
                                setIsLogin(true);
                              }}
                              key={`${index}-login`}
                            />
                          </Hidden>
                        ) : (
                          <ProfileMenu
                            authContext={authContext}
                            navigate={navigate}
                            key={`${index}-profile`}
                          />
                        )}
                      </>
                    );
                  }

                  if (item?.title === "Admin") return <></>;

                  if (item?.title === "More") return <></>;
                  return <HeaderMenuButton key={index} {...item} />;
                })}
            </Box>
            {getEnvironmentVariable("LEFT_ALIGN_MENU") === "true" && (
              <Box sx={{ flexGrow: 1 }}></Box>
            )}
          </Hidden>

          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={() => {
              setDrawerOpen(!drawerOpen);
            }}
            size="large"
            sx={{ position: "absolute", right: 1 }}
          >
            <BCPBars style={{ "font-size": "18px", width: "18px" }} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="right"
        onClose={toggleDrawer(false)}
        open={drawerOpen}
        variant="temporary"
        PaperProps={{
          sx: {
            backgroundColor: "drawer.background",
            width: ["100%", "70%", "40%", "25%"],
            top: globalVariables().menuArrayTop?.length
              ? [56, 64, 116]
              : [56, 64],
            height: globalVariables().menuArrayTop?.length
              ? ["calc(100% - 56px)", "calc(100% - 64px)", "calc(100% - 116px)"]
              : ["calc(100% - 56px)", "calc(100% - 64px)"],
            overflowX: "clip",
          },
        }}
      >
        <Content />
      </Drawer>
      <Hidden lgDown lgUp={Boolean(!itemsMiddle || itemsMiddle.length === 0)}>
        <Drawer
          anchor="left"
          open
          variant="persistent"
          PaperProps={{
            sx: {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "banner.main"
                  : "drawer.background",
              width: 256,
              top: globalVariables().menuArrayTop?.length ? [64, 64, 116] : 64,
              height: globalVariables().menuArrayTop?.length
                ? [
                    "calc(100% - 64px)",
                    "calc(100% - 64px)",
                    "calc(100% - 116px)",
                  ]
                : "calc(100% - 64px)",
              overflowX: "clip",
            },
          }}
        >
          <Content hideTop />
        </Drawer>
      </Hidden>
    </>
  );
}
